import React, { useState } from "react";
import { withRouter } from "react-router-dom";
import { loginUser, resetStaffPassword } from "../../../_actions/user_actions";
import { Formik } from 'formik';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import * as Yup from 'yup';
import { Form, Input, Button, Checkbox, Typography, message } from 'antd';
import { useDispatch } from "react-redux";
import './Login.css';

const { Title } = Typography;

function LoginPage(props) {
  const dispatch = useDispatch();
  const rememberMeChecked = localStorage.getItem("rememberMe") ? true : false;

  const [formErrorMessage, setFormErrorMessage] = useState('')
  const [rememberMe, setRememberMe] = useState(rememberMeChecked)
  const [isLoginSuccess, setIsLoginSuccess] = useState(false) // Add success state

  const handleRememberMe = () => {
    setRememberMe(!rememberMe)
  };

  const handleReset = (email) => {
    dispatch(resetStaffPassword({ "_id": email })).then(response => {
      console.log(response)
      if (response.payload) {
        message.error(response.payload)
      }
      else message.success("If your email is valid you should have a new password in your inbox. The message may be in your spam filter")
    })
  }

  const initialEmail = localStorage.getItem("rememberMe") ? localStorage.getItem("rememberMe") : '';

  return (
    <Formik
      initialValues={{
        email: initialEmail,
        password: '',
      }}
      validationSchema={Yup.object().shape({
        email: Yup.string()
          .min(4, "Must be at least 4 characters")
          .matches(/@/, "Must contain '@' symbol")
          .required('Email or User Name is required'),
        password: Yup.string()
          .min(6, 'Password must be at least 6 characters')
          .required('Password is required'),
      })}
      onSubmit={(values, { setSubmitting, setFieldValue, setFieldError, setErrors, setStatus }) => {
        // Clear any existing error messages and form errors
        setFormErrorMessage('');
        setErrors({});
        setStatus(null);
        setIsLoginSuccess(false);

        let dataToSubmit = {
          user: values.email.toLowerCase(),
          passwd: values.password
        };

        dispatch(loginUser(dataToSubmit))
          .then(response => {
            console.log('Login response full:', response);
            console.log('Response payload:', response.payload);

            // Check if login was successful by looking at different possible success indicators
            const isSuccess = response.payload && (
              response.payload.userId ||
              response.payload.user ||
              response.payload.success === true ||
              response.payload.loginSuccess === true ||
              (response.payload.success !== false && !response.payload.message && !response.payload.error)
            );

            if (isSuccess) {
              console.log('Login successful, setting success state...');

              // Set success state to prevent validation errors
              setIsLoginSuccess(true);
              setFormErrorMessage('');
              setErrors({});
              setStatus({ success: true });

              // Store user data
              if (response.payload.userId) {
                window.localStorage.setItem('userId', response.payload.userId);
              } else if (response.payload.user) {
                if (response.payload.user._id) {
                  window.localStorage.setItem('userId', response.payload.user._id);
                } else if (response.payload.user.id) {
                  window.localStorage.setItem('userId', response.payload.user.id);
                }
              }

              // Handle remember me
              if (rememberMe === true) {
                window.localStorage.setItem('rememberMe', values.email);
              } else {
                localStorage.removeItem('rememberMe');
              }

              // Use setTimeout to ensure state updates before redirect
              setTimeout(() => {
                props.history.push("/");
              }, 100);
              return;
            }

            // Handle error cases
            console.log('Login failed, showing error...');
            setIsLoginSuccess(false);
            setFieldValue('password', '');

            let errorMsg = 'Invalid credentials. Please check your email/username and password.';

            // Only use server message if it's more user-friendly than our default
            if (response.payload && response.payload.message) {
              const serverMsg = response.payload.message.toLowerCase();
              // Check if server message contains generic/technical terms
              if (serverMsg.includes('failed to check') ||
                serverMsg.includes('credential') ||
                serverMsg.includes('authentication failed') ||
                serverMsg.includes('login failed')) {
                // Use our user-friendly message instead
                errorMsg = 'Wrong email/username or password. Please try again.';
              } else if (serverMsg.includes('user not found') ||
                serverMsg.includes('email not found') ||
                serverMsg.includes('username not found')) {
                errorMsg = 'User not found. Please check your email/username or register for a new account.';
              } else if (serverMsg.includes('password') && serverMsg.includes('incorrect')) {
                errorMsg = 'Wrong password. Please try again.';
              } else if (serverMsg.includes('account') && (serverMsg.includes('locked') || serverMsg.includes('disabled'))) {
                errorMsg = response.payload.message; // Use server message for account status
              } else {
                // For other specific server messages that might be helpful
                errorMsg = response.payload.message;
              }
            } else if (response.payload && response.payload.error) {
              const serverError = response.payload.error.toLowerCase();
              if (serverError.includes('failed to check') ||
                serverError.includes('credential') ||
                serverError.includes('authentication failed')) {
                errorMsg = 'Wrong email or password. Please try again.';
              } else {
                errorMsg = response.payload.error;
              }
            }

            setFormErrorMessage(errorMsg);
            setSubmitting(false);
          })
          .catch(err => {
            console.error('Login error:', err);
            // Clear password field on error
            setFieldValue('password', '');

            let errorMessage = 'Unable to connect to server. Please check your internet connection and try again.';

            if (err.response) {
              // Server responded with error status
              if (err.response.status === 401) {
                errorMessage = 'Invalid credentials. Please check your email/username and password.';
              } else if (err.response.status === 404) {
                errorMessage = 'User not found. Please check your email/username or register for a new account.';
              } else if (err.response.status === 500) {
                errorMessage = 'Server error. Please try again later.';
              } else if (err.response.data && err.response.data.message) {
                errorMessage = err.response.data.message;
              }
            } else if (err.request) {
              // Network error
              errorMessage = 'Network error. Please check your internet connection and try again.';
            }

            setFormErrorMessage(errorMessage);

            setTimeout(() => {
              setFormErrorMessage("")
            }, 5000);
            setSubmitting(false);
          });
      }}
    >
      {props => {
        const {
          values,
          touched,
          errors,
          isSubmitting,
          handleChange,
          handleBlur,
          handleSubmit,
          status
        } = props;

        // Don't show validation errors if login was successful
        const showErrors = !isLoginSuccess && !status?.success;

        return (
          <div className="login-container fade-in">
            <div className="login-form-wrapper form-transition">
              <Title level={2} className="login-title">Log In</Title>
              <p className="login-subtitle">Sign in to your UBuck account</p>

              <Form className="login-form" onSubmit={handleSubmit}>
                <Form.Item required>
                  <Input
                    id="email"
                    prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                    placeholder="Enter your email or User Name"
                    type="username"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      showErrors && errors.email && touched.email ? 'text-input error input-transition' : 'text-input input-transition'
                    }
                  />
                  {showErrors && errors.email && touched.email && (
                    <div className="login-input-feedback error-fade-in">{errors.email}</div>
                  )}
                </Form.Item>

                <Form.Item required>
                  <Input
                    id="password"
                    prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                    placeholder="Enter your password"
                    type="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      showErrors && errors.password && touched.password ? 'text-input error input-transition' : 'text-input input-transition'
                    }
                  />
                  {showErrors && errors.password && touched.password && (
                    <div className="login-input-feedback error-fade-in">{errors.password}</div>
                  )}
                </Form.Item>

                {formErrorMessage && !isLoginSuccess && (
                  <div className="login-form-error error-fade-in">{formErrorMessage}</div>
                )}

                <div className="login-actions">
                  <Checkbox id="rememberMe" onChange={handleRememberMe} checked={rememberMe}>
                    Remember me
                  </Checkbox>
                  <a className="login-form-forgot link-transition" onClick={() => { handleReset(values.email) }}>
                    Staff Forgot Password
                  </a>
                </div>

                <div className="login-submit-section">
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="login-form-button button-transition"
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                  >
                    {isSubmitting ? 'Signing in...' : 'Log in'}
                  </Button>
                </div>

                <div className="login-register-link">
                  Or <a href="/register" className="link-transition">Register Now!</a>
                </div>
              </Form>

              <div className="login-footer-links">
                <PrivacyPolicyLink />
                <TermsOfServiceLink />
                <UpdateLink />
              </div>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

const PrivacyPolicyLink = () => (
  <a href="/privacy-policy" className="privacy-policy-link">
    Privacy Policy
  </a>
);

const TermsOfServiceLink = () => (
  <a href="/terms-of-service" className="terms-of-service-link">
    Terms of Service
  </a>
);

const UpdateLink = () => (
  <a href="/updates" className="updates-link">
    Updates
  </a>
);

export default withRouter(LoginPage);


